import axios from 'axios';
import { isNumber } from 'lodash';

/**
 * @typedef {Object} ProductVariant
 * @type {Object}
 * @property {Number} id
 * @property {String} name
 * @property {String} image
 * @property {String} description
 * @property {Number} stock
 *
 * @typedef {Object} CartItem
 * @type {Object}
 * @property {Number} quantity
 * @property {ProductVariant} productVariant
 *
 * @typedef {Object} CartState
 * @type {Object}
 * @property {CartItem[]} items
 * @property {Number} projectOrderMaxQuantity
 * @property {Boolean} isCartOpen
 */

/**
 * State: Current Shopping Cart
 *
 * - items: An array representing the items in the shopping cart (CartItem objects).
 *   If 'cartItems' is not found in localStorage, 'items' is initialized as an empty array.
 *
 * - projectOrderMaxQuantity: Stores the maximum quantity limit for items in the current project.
 *
 * - isCartOpen: Indicates whether the shopping cart interface is currently open (true) or closed (false).
 *
 * Note: Any changes to the cart (like adding/removing items, changing quantities) are immediately
 * reflected and stored in localStorage to ensure persistence across sessions.
 *
 * @property {CartItem[]} items - The product variants in the cart
 * @property {Number} projectOrderMaxQuantity - The project order max quantity
 * @property {Boolean} isCartOpen - If the cart is open
 */
const state = () => ({
    items: [],
    projectOrderMaxQuantity: 1, // This value needs to be updated with the projectOrderMaxQuantity from the project
    isCartOpen: false,
    isCreatingDraftOrder: false,
    isCartLoading: false,
    isCartCheckoutButtonDisabled: false
});

// mutations
const mutations = {
    /**
     * Add a product variant to the cart
     * @param {*} state
     * @param {ProductVariant} productVariant
     */
    addProductVariantToCart(state, {
        productVariant,
        productId
    }) {
        state.items = [...state.items, {
            productVariant,
            productId,
            quantity: 1
        }];
    },
    /**
     * Remove a product variant from the cart
     * If the product is not in the cart we do nothing
     * @param {*} state
     * @param {ProductVariant} productVariant
     */
    removeProductVariantFromCart(state, productVariant) {
        state.items = state.items.filter((item) => item.productVariant.id !== productVariant.id);
    },
    /**
     * Update the quantity of a product variant in the cart
     * If the product variant is not in the cart we do nothing
     * @param {*} state
     * @param {Object} productVariantQuantity - Object with the product variant and the quantity
     * @throws Error if the quantity is less or equal to 0
     */
    setProductVariantQuantity(state, { productVariant, quantity }) {
        state.items = state.items
            .map((item) => {
                if (item.productVariant.id === productVariant.id) {
                    return {
                        ...item,
                        quantity
                    };
                }
                return item;
            });
    },
    /**
     * Set the cart items
     * @param {*} state
     */
    setCartItems(state, items) {
        state.items = [...items];
    },
    /**
     * Update the projectOrderMaxQuantity
     * @param {*} state
     * @param {Number} quantity
     */
    setProjectOrderMaxQuantity(state, quantity) {
        state.projectOrderMaxQuantity = quantity;
    },
    /**
     * Set the cart open state
     * @param {*} state
     * @param {Boolean} isOpen
     */
    setCartOpen(state, isOpen) {
        state.isCartOpen = isOpen;
    },
    /**
     * Set the isCreatingDraftOrder state
     * @param {*} state
     * @param {Boolean} isCreatingDraftOrder
     */
    setIsCreatingDraftOrder(state, isCreatingDraftOrder) {
        state.isCreatingDraftOrder = isCreatingDraftOrder;
    },
    /**
     * Set the isCheckoutButtonDisabled state
     * @param {*} state
     * @param {Boolean} isCheckoutButtonDisabled
     */
    setCartCheckoutButtonDisabled(state, isCheckoutButtonDisabled) {
        state.isCartCheckoutButtonDisabled = isCheckoutButtonDisabled;
    },
    /**
     * Set the isCartLoading state
     * @param {*} state
     * @param {Boolean} isCartLoading
     */
    setCartLoading(state, isCartLoading) {
        state.isCartLoading = isCartLoading;
    }
};

// actions
const actions = {
    /**
     * Add a product variant to the cart.
     * If the product variant is already in the cart we increase the quantity by 1
     * Update the localStorage at the end
     * @param {*} param0
     * @param {*} productVariant
     * @throws Error if the projectOrderMaxQuantity is reached
     */
    addProductVariantToCart({ commit, getters }, {
        productVariant,
        productId
    }) {
        // before adding the product variant we should validate with the project orderMaxQuantity
        // and prevent the addition of the product variant if the orderMaxQuantity is reached
        const productVariantsQuantityInCart = getters.totalQuantity;
        if (productVariantsQuantityInCart >= getters.projectOrderMaxQuantity) {
            throw new Error('You have reached the limit of products that can be added to the cart');
        }
        const productVariantInCart = getters.items.find((item) => item.productVariant.id === productVariant.id);
        if (!productVariantInCart) {
            // if the product variant is not in the cart we add it
            commit('addProductVariantToCart', {
                productVariant,
                productId
            });
        } else {
            // if the product variant is already in the cart we increase the quantity by 1
            commit('setProductVariantQuantity', { productVariant, quantity: productVariantInCart.quantity + 1 });
        }
    },
    /**
     * Remove a product variant from the cart
     * If the product variant is not in the cart we do nothing
     * Update the localStorage at the end
     * @param {*} param0
     * @param {*} product
     */
    removeProductVariantFromCart({ commit, getters }, productVariant) {
        const productVariantInCart = getters.items.find((item) => item.productVariant.id === productVariant.id);
        if (productVariantInCart) {
            commit('removeProductVariantFromCart', productVariant);
        }
    },
    /**
     * Decrease the quantity of a product variant in the cart by 1
     * If the product variant is not in the cart we do nothing
     * If the current quantity is 1 we remove the product variant from the cart
     * Update the localStorage at the end
     * @param {*} param0
     * @param {*} product
     */
    decreaseProductVariantQuantity({ getters, commit }, productVariant) {
        const productVariantInCart = getters.items.find((item) => item.productVariant.id === productVariant.id);
        if (productVariantInCart) {
            if (productVariantInCart.quantity > 1) {
                // if the quantity is greater than 1 we decrease the quantity by 1
                commit('setProductVariantQuantity', {
                    productVariant,
                    quantity: productVariantInCart.quantity - 1
                });
            } else {
                // if the quantity is less than 1 we remove the product variant from the cart
                commit('removeProductVariantFromCart', productVariant);
            }
        }
    },
    /**
     * Increase the quantity of a product variant in the cart by 1
     * If the product variant is not in the cart we do nothing
     * Update the localStorage at the end
     * @param {*} param0
     * @param {*} product
     */
    increaseProductVariantQuantity({ getters, commit }, productVariant) {
        // before increase the quantity we should validate with the project orderMaxQuantity
        const productVariantsQuantityInCart = getters.totalQuantity;
        if (productVariantsQuantityInCart >= getters.projectOrderMaxQuantity) {
            throw new Error('You have reached the limit of products that can be added to the cart');
        }
        const productVariantInCart = getters.items.find((item) => item.productVariant.id === productVariant.id);
        if (productVariantInCart) {
            commit('setProductVariantQuantity', {
                productVariant,
                quantity: productVariantInCart.quantity + 1
            });
        }
    },
    /**
     * Reset the cart
     * @param {*} param0
     */
    resetCart({ commit }) {
        commit('setCartItems', []);
    },
    /**
     * Update the projectOrderMaxQuantity
     * @param {*} param0
     * @param {*} quantity
     */
    updateProjectOrderMaxQuantity({ commit }, quantity) {
        if (!isNumber(quantity)) {
            throw new Error('The quantity must be a number');
        }
        commit('setProjectOrderMaxQuantity', quantity);
    },
    /**
     * Open the cart
     * @param {*} param0
     */
    openCart({ commit }) {
        commit('setCartOpen', true);
    },
    /**
     * Close the cart
     * @param {*} param0
     */
    closeCart({ commit }) {
        commit('setCartOpen', false);
    },
    /**
     * Fetch the variantId by given product and selectedOptions.
     * @param {Object} product
     * @param {Array} selectedOptions
     * @returns {Promise.<ProductVariant>}
     */
    async fetchProductVariantInfoByOptions(_, { handle, product, selectedOptions }) {
        try {
            // get the variantId by given product and selectedOptions
            const { data } = await axios.post(`/campaigns/${handle}/fulfillmentLookupVariant?productId=${product.id}`, {
                options: selectedOptions
            });
            return data;
        } catch (unusedError) {
            throw new Error('Error fetching the product variant');
        }
    },
    /**
     * Upsert draft order
     * with the current items in the cart
     * @param {*} param0
     * @param {*} handle
     * @returns {Promise} - The new draft order
     */
    async upsertDraftOrderWithCartItems({ commit, getters }, handle) {
        try {
            commit('setIsCreatingDraftOrder', true);
            const draftOrder = await axios.put(`/campaigns/${handle}/fulfillmentUpsertDraftOrder`, {
                lineItems: getters.items.map((item) => ({
                    variantId: item.productVariant.id,
                    quantity: item.quantity
                }))
            }).then((r) => r.data);
            // if everything is ok we reset the cart
            commit('setCartItems', []);
            return draftOrder;
        } catch (unusedError) {
            throw new Error('Error creating the draft order');
        } finally {
            commit('setIsCreatingDraftOrder', false);
        }
    },
    /**
     * Initialize cart for the given campaign
     * Loads the existing cart from local storage based on the campaign handle
     * Multiple carts are supported by storing the cart items in
     * localStorage with the campaign handle as the key
     * @param {Object} context
     * @param {Object} campaign
     * @param {Array} availableProducts
     */
    initializeCartForCampaign({ commit }, {
        campaign,
        availableProducts
    }) {
        const { handle } = campaign;
        const localStorageCartItems = localStorage.getItem(`${handle}-cartItems`);
        if (!localStorageCartItems) {
            commit('setCartItems', []);
            return;
        }
        const currentCartItems = JSON.parse(localStorageCartItems);
        const items = currentCartItems.filter((item) => availableProducts.find(
            (availableProduct) => availableProduct.id === item.productId
        ));
        commit('setCartItems', items);
    }
};

// getters
const getters = {
    /**
     * Get the items in the cart
     * @param {*} state
     * @returns
     */
    items(state) {
        return state.items;
    },
    /**
     * Get the current projectOrderMaxQuantity
     * @param {*} state
     * @returns Number
     */
    projectOrderMaxQuantity(state) {
        return state.projectOrderMaxQuantity;
    },
    /**
     * Get the total quantity of products in the cart
     * @param {*} state
     * @returns Number
     */
    totalQuantity(state) {
        return state.items.reduce((total, item) => total + item.quantity, 0);
    },
    /**
     * Get if the cart is open
     * @param {*} state
     * @returns Boolean
     */
    isCartOpen(state) {
        return state.isCartOpen;
    },
    /**
     * Check if the cart addition is disabled or not
     * It'll check for the limits
     * @param {*} state
     * @returns Boolean
     */
    isCartAdditionDisabled(state, getters) {
        return getters.totalQuantity >= state.projectOrderMaxQuantity;
    },
    /**
     * Return if a product variant is in the cart
     * @param {*} state
     * @returns Boolean
     */
    isProductInCart: (state) => (productVariant) => state.items.some((item) => item.productVariant.id === productVariant.id),
    /**
     * Return if the cart is loading
     * @param {*} state
     * @returns Boolean
     */
    isCartLoading: (state) => state.isCartLoading,
    /**
     * Return if the cart checkout button is disabled
     * @param {*} state
     * @returns Boolean
     */
    isCartCheckoutButtonDisabled: (state) => state.isCartCheckoutButtonDisabled
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
